import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { AppRoutes } from '@mri-platform/dsg/core';
import { isNotNullOrUndefined, keysOf, LogoutPromptService } from '@mri-platform/shared/core';
import { PageFooterComponent, ShellComponent as SharedShell } from '@mri-platform/shared/shell';
import { HeaderLogo } from '@mri-platform/shared/ui-main-nav-bar';
import { AuthService, User } from '@mri/angular-wfe-proxy-oidc';
import { RxState } from '@rx-angular/state';
import { selectSlice } from '@rx-angular/state/selections';
import { combineLatest, Observable } from 'rxjs';
import { tag } from 'rxjs-spy/operators';
import { map } from 'rxjs/operators';

interface ComponentState {
  userInfo: User;
  isOperationalUser: boolean;
  currentClientId: string;
}

type PublicState = ComponentState;

interface Projections {
  currentClientInfo: string;
}

type ViewModel = PublicState & Projections;

const initialPublicState: PublicState = {
  userInfo: undefined as never,
  isOperationalUser: true,
  currentClientId: ''
};

const initialState: ComponentState = {
  ...initialPublicState
};

@Component({
  template: `
    <!--
  IMPORTANT: do NOT use an \`@if\` at the level of \`mri-shared-shell\` component. Doing so will result in a delay in rendering
  the \`router-outlet\` inside of shell component template. Such a delay will cause the constructor of the routed component
  to be executed AFTER the router events for that component having already being fired. This is problematic if that routed component
  needs to listen to these router events in order to perform some logic EG the logic inside of \`MasterListBasePageComponent.selectedItemId\`
  -->
    <mri-shared-shell [headerLogo]="headerLogo">
      @if (vm$ | async; as vm) {
        <mri-shared-page-footer
          pageFooter
          [user]="vm.userInfo"
          [currentClientId]="vm.currentClientInfo"
          [isEnableSwitchClient]="vm.isOperationalUser"
          (handleLogout)="handleLogout()"
          (openClientDrawer)="openClientDrawer()"
        >
        </mri-shared-page-footer>
      }
    </mri-shared-shell>

    <router-outlet name="modal"></router-outlet>
  `,
  styles: [
    `
      :host {
        width: 100%;
        height: 100%;
        display: block;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RxState],
  imports: [AsyncPipe, RouterOutlet, PageFooterComponent, SharedShell],
  standalone: true
})
export class ShellComponent {
  vm$: Observable<ViewModel>;
  constructor(
    private state: RxState<ComponentState>,
    private authService: AuthService,
    private logoutPromptService: LogoutPromptService,
    private router: Router
  ) {
    // Set initial state in RxState
    this.state.set(initialState);

    // Connect any observable-driven items to state for items in ComponentState...
    this.state.connect(this.authService.userLoaded$, (_, userInfo) => ({
      userInfo,
      isOperationalUser: userInfo.canSwitchClients || (userInfo.onboardedTenants?.length ?? 0) > 1
    }));

    this.state.connect('currentClientId', this.authService.currentClientId$.pipe(isNotNullOrUndefined()));

    // Create projections (calculations from ComponentState)...
    const currentClientInfo$ = this.state.select(selectSlice(['userInfo', 'currentClientId'])).pipe(
      map(({ userInfo, currentClientId }) => {
        if (currentClientId === userInfo.clientName) return currentClientId;
        return `${currentClientId} - ${userInfo.clientName}`;
      })
    );

    // Create ViewModel (Projections + PublicState)...

    const publicState$ = this.state.select(selectSlice(keysOf(initialPublicState)));
    this.vm$ = combineLatest([publicState$, currentClientInfo$]).pipe(
      map(([publicState, currentClientInfo]) => ({ ...publicState, currentClientInfo })),
      tag('vm')
    );
    // side effects if any...
    // none
  }

  headerLogo: HeaderLogo = {
    url: 'assets/images/agora_insights_nav_logo.svg',
    text: 'Agora Insights'
  };

  async handleLogout() {
    const canProceed = await this.logoutPromptService.showLogoutPrompt$().toPromise();
    if (canProceed) {
      this.authService.logOut();
    }
  }

  openClientDrawer() {
    this.router.navigate(['', { outlets: { drawer: [AppRoutes.SwitchClient.path] } }], {
      queryParamsHandling: 'preserve'
    });
  }
}
